import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTables } from '../hooks/useTables';
import type { StaffTable, TableStatus } from '../hooks/useTables';
import BottomNav from '../components/BottomNav';
import { useNotifications } from '../hooks/useNotifications';
import { usePushSubscription } from '../hooks/usePushSubscription';
import ChannelBadge from '../components/ChannelBadge';

type Filter = 'all' | TableStatus;

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all',              label: 'Toutes' },
  { id: 'free',             label: 'Libres' },
  { id: 'occupied',         label: 'Occupées' },
  { id: 'payment_received', label: 'Paiement reçu' },
  { id: 'needs_attention',  label: 'Attention' },
];

const STATUS_STYLES: Record<TableStatus, { container: string; text: string }> = {
  free:             { container: 'bg-surface',                text: 'text-on-surface-variant' },
  occupied:         { container: 'bg-surface-container',      text: 'text-on-surface' },
  payment_received: { container: 'bg-inverse-surface',        text: 'text-inverse-on-surface' },
  needs_attention:  { container: 'bg-surface-container-high', text: 'text-on-surface' },
  reserved:         { container: 'bg-surface-container-low',  text: 'text-on-surface-variant' },
};

const STATUS_LABELS: Record<TableStatus, string> = {
  free:             'Libre',
  occupied:         'Occupée',
  payment_received: 'Payée',
  needs_attention:  'À encaisser',
  reserved:         'Réservée',
};

function TableCard({ table, onClick }: { table: StaffTable; onClick: () => void }) {
  const style = STATUS_STYLES[table.status];
  const isAttention = table.status === 'needs_attention';

  return (
    <button
      onClick={onClick}
      className={`h-[100px] w-full flex flex-col justify-between p-3 text-left transition-none ${style.container}`}
      style={{ border: '0.5px solid #000', position: 'relative' }}
    >
      {isAttention && (
        <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-error animate-pulse" />
      )}
      <div>
        <p className="text-sm font-bold text-on-surface-strong">Table {table.table_number}</p>
        <p className={`text-[10px] ${style.text}`}>
          {table.capacity} pers{table.section ? ` · ${table.section}` : ''}
        </p>
      </div>
      <div className="flex items-end justify-between">
        {table.current_amount != null ? (
          <span className="text-sm font-bold tabular-nums text-on-surface-strong">
            {table.current_amount.toLocaleString('fr-MA', { minimumFractionDigits: 2 })} MAD
          </span>
        ) : (
          <span className={`text-[10px] font-semibold uppercase tracking-wider ${style.text}`}>
            {STATUS_LABELS[table.status]}
          </span>
        )}
        {table.payment_channel && (
          <ChannelBadge channel={table.payment_channel} size="sm" />
        )}
      </div>
    </button>
  );
}

export default function FloorPlanPage() {
  const { session, logout } = useAuth();
  const { tables, loading } = useTables(session?.storeId ?? '');
  const { unreadCount } = useNotifications(session?.storeId ?? '');
  const push = usePushSubscription({
    storeId: session?.storeId ?? null,
    employeeId: session?.employeeId ?? null,
  });
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('all');

  const filtered = filter === 'all' ? tables : tables.filter((t) => t.status === filter);

  return (
    <div className="min-h-screen bg-background pb-[76px]">
      {/* Header */}
      <header
        className="sticky top-0 z-30 bg-surface flex items-center justify-between px-4 h-12"
        style={{ borderBottom: '0.5px solid #e5e7eb' }}
      >
        <img src="/tapnpay-brand.png" alt="TapNpay" className="max-h-[24px] object-contain" />
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-on-surface">{session?.name}</span>
          <button
            onClick={logout}
            className="text-on-surface-variant min-h-[36px] min-w-[36px] flex items-center justify-center"
            aria-label="Déconnexion"
          >
            <span className="material-symbols-outlined text-[20px]">logout</span>
          </button>
        </div>
      </header>

      {push.showBanner && (
        <div
          className="px-4 py-3 flex items-center justify-between gap-3 bg-surface-container-low"
          style={{ borderBottom: '0.5px solid #000' }}
        >
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold uppercase tracking-wider text-on-surface-strong">
              Recevoir les alertes
            </p>
            <p className="text-[11px] text-on-surface-variant">
              Activez les notifications pour ne rater aucune commande.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void push.enable()}
            className="min-h-[40px] px-3 text-[11px] font-semibold uppercase tracking-wider text-on-primary bg-primary"
          >
            Activer
          </button>
          <button
            type="button"
            onClick={push.dismiss}
            className="min-h-[40px] min-w-[36px] flex items-center justify-center text-on-surface-variant"
            aria-label="Fermer"
          >
            <span className="material-symbols-outlined text-[18px]">close</span>
          </button>
        </div>
      )}

      {/* Filtres */}
      <div
        className="flex gap-2 px-4 py-2 overflow-x-auto hide-scrollbar"
        style={{ borderBottom: '0.5px solid #e5e7eb' }}
      >
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`shrink-0 min-h-[36px] px-3 text-xs font-semibold uppercase tracking-wider transition-none ${
              filter === f.id
                ? 'bg-primary text-on-primary'
                : 'bg-surface text-on-surface'
            }`}
            style={{ border: '0.5px solid #e5e7eb' }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Grille */}
      <div className="p-4">
        {loading ? (
          <div className="flex justify-center py-10">
            <div className="h-8 w-8 animate-scanpay-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-sm text-on-surface-variant py-10">
            {filter === 'all' ? 'Aucune table configurée.' : 'Aucune table dans cette catégorie.'}
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {filtered.map((t) => (
              <TableCard
                key={t.id}
                table={t}
                onClick={() => navigate(`/table/${t.id}`)}
              />
            ))}
          </div>
        )}
      </div>

      <BottomNav unreadCount={unreadCount} />
    </div>
  );
}
